/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, { useMemo, useEffect, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { Avatar, Dropdown } from 'antd';
import { LoadingOutlined, UserOutlined } from '@ant-design/icons';

import { useAppSelector } from '@/hooks/useRedux';
import { useLogout } from '@/components/auth/LogoutProvider';
import { IoLogOutOutline } from 'react-icons/io5';

type UserLike = {
  username?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  avatar?: string | null;
} | null;

type Props = {
  user?: UserLike;
  compact?: boolean;
};

const UserMenu: React.FC<Props> = ({ user: userProp, compact = false }) => {
  const router = useRouter();
  const { requestLogout } = useLogout();
  const [navigating, startNavigation] = useTransition();

  const reduxUser = useAppSelector((s: any) => s.auth?.signin?.user);
  const user = userProp ?? reduxUser;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const displayName =
      (user?.first_name && user?.last_name
        ? `${user.first_name} ${user.last_name}`
        : user?.first_name) || user?.username;

    if (displayName) {
      localStorage.setItem('username', displayName.trim());
    }

    if (user) {
      try {
        localStorage.setItem('userProfile', JSON.stringify(user));
      } catch {
      }
    }
  }, [user]);

  const storedProfile =
    typeof window !== 'undefined'
      ? (() => {
          try {
            const raw = localStorage.getItem('userProfile');
            return raw ? JSON.parse(raw) : null;
          } catch {
            return null;
          }
        })()
      : null;

  const fullName =
    (user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`
      : user?.first_name) ||
    user?.username ||
    (typeof window !== 'undefined'
      ? localStorage.getItem('username') || 'User'
      : 'User');

  const email = user?.email || storedProfile?.email || '---';

  const initials = useMemo(() => {
    const n = (fullName || 'U').trim();
    const parts = n.split(' ');
    const i = (parts[0]?.[0] || 'U') + (parts[1]?.[0] || '');
    return <i className="capitalize">{i}</i>;
  }, [fullName]);

  const items = [
    { key: 'profile', label: 'My Profile' },
    { key: 'account', label: 'My Account' },
    { type: 'divider' as const },
    {
      key: 'logout',
      label: 'Logout',
      danger: true,
      icon: <IoLogOutOutline />,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === 'profile')
      startNavigation(() => router.push('/account/profile'));
    else if (key === 'account') startNavigation(() => router.push('/account'));
    else if (key === 'logout') requestLogout();
  };

  return (
    <Dropdown
      overlayStyle={{ zIndex: 1050 }}
      menu={{ items, onClick: handleMenuClick }}
      placement="bottomRight"
      trigger={['click']}
    >
      <div
        aria-busy={navigating || undefined}
        className="inline-flex items-center gap-3 bg-amber-50 px-1 cursor-pointer py-1 w-full rounded-md hover:bg-amber-100 transition z-40!"
      >
        <Avatar
          size="large"
          icon={!initials ? <UserOutlined /> : undefined}
          className="flex-shrink-0"
        >
          {initials}
        </Avatar>

        <div className="flex-1 min-w-0 flex flex-col">
          <span
            className={`text-sm font-semibold text-gray-900 leading-tight truncate ${
              compact ? 'max-w-[8rem] xl:max-w-[14rem]' : ''
            }`}
          >
            {fullName}
          </span>
          <span
            className={`text-xs text-gray-500 leading-tight truncate ${
              compact ? 'hidden xl:block xl:max-w-[14rem]' : ''
            }`}
          >
            {email || '---'}
          </span>
        </div>

        {navigating ? (
          <LoadingOutlined
            spin
            aria-label="Opening"
            className="ml-1 text-lg flex-shrink-0 text-[var(--color-primary)]"
          />
        ) : (
          <IoLogOutOutline className="ml-1 text-gray-400 text-lg flex-shrink-0" />
        )}
      </div>
    </Dropdown>
  );
};

export default UserMenu;
